import { Link } from "@tanstack/react-router";
import { useCallback, useState, type ReactNode } from "react";

import {
  BlockedMenuItem,
  IconMenuButton,
  IconMenuButtonContent,
  IconMenuButtonTrigger,
  IconMenuLoading,
  MoreVertGlyph,
  PressableMenuItem,
} from "@/components/ui/icon-menu-button";
import { useCatalogItemDetail } from "@/hooks/catalog-items";
import { useItemsError } from "@/hooks/items-error";
import type { CatalogListItem } from "@/lib/catalog-item";
import { catalogItemMenuGates, type CatalogItemMenuGate } from "@/lib/catalog-item-actions";

// The catalog list's per-item actions menu — `item-actions-button.tsx` — as it
// hangs in the top-right corner of every card and every list row.
//
// Four rows, in the donor's order, and nothing else: **Add variant is commented
// out** in the source (handler and row both, `item-actions-button.tsx:123-125`)
// and there is **no Group entry** at all, so neither ships (#450 R4, R5). The
// donor also accepts an `is_hidden` flag it never draws a row for.
//
// Not the item-search island's `ActionsMenu.tsx` (#450 R1): that is the spec
// editor's own menu. **One piece of it is borrowed, by owner ruling (#669 D6)**:
// a Division Editor's Edit and Delete follow the item's own `canEdit`, read when
// the menu is launched. The donor's list asks `canEdit` of a row that carries no
// divisions (`items-card-list.tsx:112`), which is false for every Division Editor
// and hides this whole menu from them. #450 R7 showed it by role instead. So a
// Division Editor sees it now, and waits on the item's detail read, as the
// item-search menu waits on its usage read (`ActionsMenu.tsx:36-52`, `:149-155`).
// Nobody else waits: the other two roles never narrow.
//
// **The trigger, the menu box and the refused rows are the shared
// `icon-menu-button` pieces** (#456) — the same three the details page composes.
// This menu is the `plain` box: the donor passes `variant='outlined'` and MUI's
// `IconButton` has no such prop, so the list's trigger is a borderless circle
// while the details page's really is outlined.
//
// **Gating lives in `@/lib/catalog-item-actions`**, not here. Four states across
// three rows, two of them owner-ruled parity breaks with a precedence rule
// between them (#450 R2, R3, R10), is arithmetic — and arithmetic in JSX can
// only be tested through a rendered menu.

/**
 * What a list view needs to draw this menu — **present or absent as one thing**,
 * exactly as `ItemSelectionProps` models batch mode.
 *
 * A view has the whole bundle or none of it, and the page decides which: the
 * card's own field tests and the table view render without any. The two bundles
 * are also **mutually exclusive by construction at the page**, which is what
 * makes the donor's `{selectable && <tick/>} / {!selectable && showItemActions
 * && <menu/>}` true without either component asking about the other.
 */
export interface ItemActionsProps {
  actions?: ItemActions;
}

/** The menu's three verbs, plus the one permission that is not on the row. */
export interface ItemActions {
  /**
   * Whether this user may set core status at all — `canBulkEditCatalogItems`,
   * the admin-or-national-editor pair Python's `assert_may_set_core_status`
   * enforces.
   *
   * On the bundle rather than read from a hook here, because it is a property of
   * the session and this component is rendered once per row: twenty cards would
   * be twenty reads of one answer. The one read this menu does make is per
   * item, and only a Division Editor's (`narrowedEditor`).
   */
  canSetCoreStatus: boolean;
  /**
   * Whether the edit ladder narrows this user by division — a Division Editor,
   * the one role Python's `assert_may_edit_item` asks about an item's divisions.
   * Their Edit item and Delete item wait on the item's own `mayEdit`, read when
   * the menu is launched (#669 D6). Absent or false, nothing is read.
   */
  narrowedEditor?: boolean;
  onMarkCore: (item: CatalogListItem) => void;
  onRemoveCore: (item: CatalogListItem) => void;
  onDelete: (item: CatalogListItem) => void;
}

type ItemActionsMenuProps = ItemActions & {
  /**
   * The item to act on — the card's or row's **own** item, never the variant
   * currently being previewed.
   *
   * The donor is deliberate about this and says so in a comment: "the admin
   * actions menu ... always operates on this card's item rather than the variant
   * currently being previewed" (`item-card.tsx:52-56`). So during a preview the
   * visible badges describe the variant while this menu's gating, its verbs and
   * its Edit link all still describe the base item. The opposite of the details
   * page, where the menu does follow the variant.
   */
  item: CatalogListItem;
};

/** One verb row: the label, or the refusal and its reason. */
function VerbRow({
  label,
  gate,
  onSelect,
}: {
  label: string;
  gate: CatalogItemMenuGate;
  onSelect: () => void;
}) {
  // A refusal with something to say. The donor's list menu has no tooltips at
  // all — these rows exist only because #450's owner-ruled breaks disable rows
  // the donor leaves enabled — so the nearest source arrangement is the details
  // menu's, and it has two settings rather than one: its *first* blocked row
  // points the box up and away from the menu with no callout
  // (`ActionMenu.tsx:176-186`, `placement='top'`), its *last* points it down and
  // away with one (`:211-215`, `placement='bottom'` with `showCallout`). What
  // both express is the same rule — a reason box never lands on a menu row.
  //
  // The two settings land here by gate rather than by index, and they agree:
  // the role gate is the two core rows, which are this menu's first two, and
  // the usage gate is `Delete item`, which is its last. Taking the last row's
  // settings for every row (as this did until #457's eighth parity pass) put
  // each box flush on the rows below it — the box for a blocked
  // `Remove core tag` covered `Edit item`'s label outright and swallowed clicks
  // on the top half of it, and `Edit item` is enabled for everyone.
  //
  // Pointing the core rows' box up cannot cover a choosable row: the only gate
  // that gives those rows anything to *say* is the role one, and it refuses
  // both of them together (R10), so the row above is always refused too. A core
  // row blocked for any other reason is silent, and falls through below.
  if (gate.kind === "role" || gate.kind === "usage") {
    const last = gate.kind === "usage";
    return (
      <BlockedMenuItem
        label={label}
        reason={gate.reason}
        side={last ? "bottom" : "top"}
        arrow={last}
        testIdPrefix="item-action"
      />
    );
  }

  // A refusal with nothing to say — the donor's own two core gates, which carry
  // no explanation of any kind. The primitive's `disabled` is right here and
  // wrong on the row above: it takes the row out of the menu's roving focus,
  // which is what MUI's disabled `MenuItem` also does, and there is no reason
  // for a keyboard user to reach a row that would tell them nothing.
  return (
    <PressableMenuItem
      data-testid={`item-action-${label}`}
      disabled={gate.kind === "silent"}
      onSelect={onSelect}
    >
      {label}
    </PressableMenuItem>
  );
}

/**
 * Whether this menu's Edit item and Delete item are on offer: `"yes"` for a
 * reader the ladder never narrows, and for a Division Editor the item's read
 * admitted; `"no"` when it refused them or failed; `"loading"` until it lands.
 */
type EditState = "yes" | "no" | "loading";

// What a failed read costs a Division Editor, in the donor's words: its
// `ERRORS.ITEM_USAGE` (`ItemSearchIsland/constants.ts:5`), raised by the usage
// read its item-search menu launches — which `item-search-actions-menu.tsx`
// ports, and raises it on the same failure.
export const LOAD_EDIT_ACCESS_ERROR =
  "Error loading item usage. Editing of item disabled. Please refresh page to try again.";

export function ItemActionsMenu({ narrowedEditor = false, ...props }: ItemActionsMenuProps) {
  return narrowedEditor ? (
    // Keyed on the item: what one item's launches learned is that item's alone,
    // so a menu handed another item in place starts unlaunched.
    <NarrowedItemActionsMenu key={props.item.id} {...props} />
  ) : (
    <ItemActionsMenuBody {...props} edit="yes" />
  );
}

/**
 * The menu for a Division Editor: the item's detail read, made each time the
 * menu is launched.
 *
 * On pointer-down, as the donor fires its query on `onMouseDownCapture`: "a very
 * strong indicator users wants to activate the menu", and earlier than the click
 * (`ActionsMenu.tsx:36-52`). The menu's opening launches as well, so a keyboard
 * opening reads too; the donor's keyboard path skips its fetch and so never
 * offers Edit. The press and the opening it causes are one read: a launch
 * during a read joins it.
 *
 * Every launch reads again, as the donor's `forceRefetch: () => true` does
 * (`itemsApi.ts:100-113`), and whatever is cached answers meanwhile, as its
 * `preferCacheValue` lets it: the read is the details page's own, so a reader
 * who has just visited the item sees its answer at once.
 */
function NarrowedItemActionsMenu(props: Omit<ItemActionsMenuProps, "narrowedEditor">) {
  const [launched, setLaunched] = useState(false);
  // Never enabled: the reads are the launches', and the query only watches the
  // cache entry they fill. An enabled one would also read on its own schedule
  // (window focus, reconnect), which the donor's lazy query never does.
  const detail = useCatalogItemDetail(props.item.id, { enabled: false });
  // A failure some other screen hit on this item is not this menu's to announce,
  // and one being retried is not a failure yet.
  const failed = launched && detail.isError && !detail.isFetching;
  useItemsError(failed, LOAD_EDIT_ACCESS_ERROR);

  const { refetch } = detail;
  const launch = useCallback(() => {
    setLaunched(true);
    void refetch({ cancelRefetch: false });
  }, [refetch]);

  // The Delete count is the read's too, as the donor's is
  // (`specificationCounts.specificationCount`, `ActionsMenu.tsx:134`): the
  // row's summary is as old as the page of results.
  const landed = detail.data;
  const item = landed ? { ...props.item, usageCount: landed.item.usageCount } : props.item;

  return (
    <ItemActionsMenuBody
      {...props}
      item={item}
      edit={editState(landed, failed)}
      onLaunch={launch}
    />
  );
}

function editState(landed: { mayEdit: boolean } | null | undefined, failed: boolean): EditState {
  // `null` is the read finding no such item — gone since the page loaded, and
  // nothing to edit.
  if (landed === null) return "no";
  if (landed) return landed.mayEdit ? "yes" : "no";
  return failed ? "no" : "loading";
}

function ItemActionsMenuBody({
  item,
  canSetCoreStatus,
  onMarkCore,
  onRemoveCore,
  onDelete,
  edit,
  onLaunch,
}: Omit<ItemActionsMenuProps, "narrowedEditor"> & { edit: EditState; onLaunch?: () => void }) {
  const gates = catalogItemMenuGates(item, canSetCoreStatus);

  let rows: ReactNode;
  if (edit === "loading") {
    // `ActionsMenu.tsx:149-155` — the trigger stays, the rows are replaced.
    rows = <IconMenuLoading />;
  } else {
    rows = (
      <>
        <VerbRow label="Mark core" gate={gates.markCore} onSelect={() => onMarkCore(item)} />
        <VerbRow
          label="Remove core tag"
          gate={gates.removeCore}
          onSelect={() => onRemoveCore(item)}
        />
        {/* Both or neither, as the item-search menu draws them under one
            `canEditItem && (…)` (`ActionsMenu.tsx:174-201`). */}
        {edit === "yes" && (
          <>
            {/* The row *is* the link — `component={RouterLink}` in the donor,
                and `asChild` here. A row that navigated from an `onSelect`
                would look identical and lose middle-click, copy-link-address,
                and the keyboard's own idea of what a link is (#450 R6).

                Typed, so a rename of the edit route is a compile error here
                rather than a dead menu entry. The donor's own href is
                `/products/:id/edit` (`navigation.ts:4-6`); Atlas's equivalent
                route is `/items/:id/edit`, already what the details page's Edit
                points at. */}
            <PressableMenuItem asChild>
              <Link
                data-testid="item-action-Edit item"
                to="/items/$itemId/edit"
                params={{ itemId: String(item.id) }}
                target="_blank"
                rel="noopener noreferrer"
              >
                Edit item
              </Link>
            </PressableMenuItem>
            <VerbRow label="Delete item" gate={gates.deleteItem} onSelect={() => onDelete(item)} />
          </>
        )}
      </>
    );
  }

  return (
    <IconMenuButton onOpenChange={(open) => open && onLaunch?.()}>
      {/* The donor's `top: 8px; right: 8px; zIndex: 3` — the batch tick's own
          corner, and the same classes it uses, because the two controls share
          the slot rather than sitting side by side.

          Named after the item, which the donor's trigger is not: it has no
          accessible name at all (`menu-button.tsx:90-96` wires none, and
          `MoreVertIcon` is `aria-hidden`). Twenty identical unnamed buttons is
          twenty controls a screen reader cannot distinguish, and the tick
          beside it already solved this the same way. */}
      <IconMenuButtonTrigger
        label={`More options for ${item.name}`}
        box="plain"
        className="absolute top-2 right-2 z-3"
        onPointerDown={onLaunch}
      >
        <MoreVertGlyph />
      </IconMenuButtonTrigger>
      <IconMenuButtonContent>{rows}</IconMenuButtonContent>
    </IconMenuButton>
  );
}
